/**
 * API service for ProductTrace frontend
 */

import { config } from '../config/environment';

const API_BASE_URL = config.apiBaseUrl;
const API_V1 = `${API_BASE_URL}/api`;

// Types
export interface Product {
  id: string;
  name: string;
  slug: string;
  brand?: string;
  model?: string;
  category: string;
  description?: string;
  retail_price?: number;
  manufacturing_cost?: number;
  currency: string;
  analysis_status: 'pending' | 'analyzing' | 'completed' | 'failed';
  analysis_progress: number;
  cost_coverage_percentage: number;
  confidence_score?: number;
  total_components: number;
  analyzed_components: number;
  major_components_count: number;
  accuracy_validated: boolean;
  corrections_made: number;
  image_url?: string;
  display_name: string;
  is_analysis_complete: boolean;
  analysis_progress_display: string;
  created_at: string;
  updated_at: string;
  analyzed_at?: string;
  // Environmental and labor data
  overall_environmental_impact?: {
    carbon_footprint_kg?: number;
    energy_consumption_kwh?: number;
    water_usage_liters?: number;
    waste_generation_kg?: number;
    recyclability_percentage?: number;
  };
  overall_labor_summary?: string;
  total_worker_count?: number;
  primary_production_countries?: Array<{
    country: string;
    percentage: number;
  }>;
  // Risk assessment data
  risk_assessment?: {
    geopolitical_risk?: string;
    geopolitical_risk_description?: string;
    supply_stability_risk?: string;
    supply_stability_description?: string;
    environmental_risk?: string;
    environmental_risk_description?: string;
    labor_risk?: string;
    labor_risk_description?: string;
    regulatory_risk?: string;
    regulatory_risk_description?: string;
    overall_risk_score?: number;
  };
  overall_risk_score?: number;
  // Supply chain concentration data
  supply_chain_concentration?: Array<{
    country: string;
    percentage: number;
    value_usd: number;
    why_concentrated: string;
    labor_summary: string;
    environmental_summary: string;
    risk_factors: string[];
    major_suppliers?: string[];
  }>;
}

export interface EnvironmentalImpact {
  carbon_footprint_kg?: number;
  energy_consumption_kwh?: number;
  water_usage_liters?: number;
  waste_generation_kg?: number;
  recyclability_percentage?: number;
  renewable_energy_percentage?: number;
}

export interface LaborRequirements {
  worker_count?: number;
  skill_level?: string;
  average_wage_usd_hour?: number;
  working_hours_per_week?: number;
  working_conditions?: string;
  safety_rating?: string;
  automation_level?: string;
}

export interface LocationDetail {
  id: string;
  name: string;
  country: string;
  country_code: string;
  production_percentage?: number;
  description?: string;
  display_name: string;
  companies?: string[];
  facilities?: Array<{
    name: string;
    address?: string;
    city?: string;
    type?: string;
    google_maps_link?: string;
  }>;
  // Environmental and labor data for this location
  worker_count?: number;
  wage_usd_hour?: number;
  renewable_energy_percentage?: number;
  waste_generation_kg?: number;
  why_here?: string;
}

export interface Component {
  id: string;
  name: string;
  slug: string;
  category?: string;
  description?: string;
  function?: string;
  estimated_cost_min?: number;
  estimated_cost_max?: number;
  estimated_cost_avg?: number;
  analysis_status: 'pending' | 'analyzing' | 'completed' | 'failed';
  sub_components_count: number;
  analyzed_sub_components: number;
  complexity_level?: string;
  manufacturing_time_days?: number;
  country_of_origin?: string;
  production_locations?: any[];
  environmental_impact?: EnvironmentalImpact;
  labor_requirements?: LaborRequirements;
  design_rationale?: string;
  is_analysis_complete: boolean;
  created_at: string;
  updated_at: string;
}

export interface ComponentDetail extends Component {
  sub_components: Component[];
  processes: ProcessDetail[];
  locations?: LocationDetail[];
  production_locations?: LocationDetail[];
}

export interface ComponentSummary extends Component {
  cost: number;
  cost_percentage?: number;
  tier: 'major' | 'secondary' | 'minor';
}

export interface ProductWithComponents extends Product {
  components: ComponentSummary[];
}

export interface ProductAnalysisRequest {
  product_name: string;
  brand?: string;
  additional_context?: string;
}

export interface ProductSuggestion {
  name: string;
  brand: string;
  model: string;
  category: string;
  description: string;
  confidence: number;
}

export interface ExistingProductMatch {
  product_id: string;
  name: string;
  brand?: string;
  slug: string;
  similarity_score: number;
  exact_match: boolean;
}

export interface ProductDisambiguationResponse {
  message: string;
  needs_disambiguation: boolean;
  resolved_name: string;
  confidence: number;
  suggestions: ProductSuggestion[];
  existing_matches: ExistingProductMatch[];
}

export interface ProductAnalysisResponse {
  message: string;
  product_id: string;
  job_id: string;
  slug: string;
  analysis_status: string;
  existing: boolean;
}

export interface ProductConfirmationRequest {
  selected_product: string;
  brand: string;
  model?: string;
  category: string;
  additional_context?: string;
}

export interface SearchResult {
  id: string;
  name: string;
  slug: string;
  brand?: string;
  display_name: string;
  analysis_status: string;
  analysis_progress: number;
}

export interface BackgroundJob {
  id: string;
  job_type: string;
  job_name: string;
  description?: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  progress: number;
  current_step?: string;
  error_message?: string;
  created_at: string;
  updated_at: string;
}

export interface ApiError {
  error: string;
  message: string;
  details?: any;
}

export interface ProcessDetail {
  id: string;
  name: string;
  slug: string;
  category?: string;
  description?: string;
  overview?: string;
  detailed_steps?: any[];
  complexity_level?: string;
  automation_level?: string;
  precision_required?: string;
  typical_duration_hours?: number;
  setup_time_hours?: number;
  total_time_hours?: number;
  cost_per_unit?: number;
  cost_currency: string;
  equipment_required?: any[];
  equipment_cost?: number;
  facility_requirements?: any[];
  workers_required?: number;
  skill_level_required?: string;
  training_time_hours?: number;
  energy_consumption_kwh?: number;
  water_usage_liters?: number;
  waste_generated_kg?: number;
  carbon_footprint_kg?: number;
  safety_requirements?: any[];
  regulatory_compliance?: any[];
  hazard_level?: string;
  quality_checks?: any[];
  defect_rate_percentage?: number;
  yield_percentage?: number;
  image_url?: string;
  additional_images?: string[];
  video_url?: string;
  meta_title?: string;
  meta_description?: string;
  tags?: string[];
  facility_locations?: Array<{
    country: string;
    percentage: number;
    company: string;
    facility_name: string;
    facility_address?: string;
    facility_city: string;
    facility_type: string;
  }>;
  created_at: string;
  updated_at: string;
}

export interface ProcessComponent {
  id: string;
  name: string;
  slug: string;
  category?: string;
  product_name?: string;
  product_slug?: string;
  order_sequence?: number;
  is_primary_process: boolean;
  process_duration_hours?: number;
  process_cost?: number;
  yield_percentage?: number;
  defect_rate_percentage?: number;
}

// Tariff and Reshoring Analysis types
export interface TariffAnalysisResponse {
  id: string;
  product_id: string;
  total_tariff_cost_usd: number;
  tariff_percentage: number;
  component_breakdown: Array<{
    component_id: string;
    component_name: string;
    hts_code: string;
    applicable_rate: number;
    tariff_cost_usd: number;
    policy_risk: string;
    alternative_sources: string[];
  }>;
  geographic_risk: {
    overall_risk: string;
    country_scores: Record<string, number>;
    highest_risk_countries: string[];
  };
  policy_scenarios: {
    current: { total_cost: number; probability: number };
    trade_war: { total_cost: number; probability: number };
    retaliation: { total_cost: number; probability: number };
    base_case: { total_cost: number; probability: number };
  };
  mitigation_strategies: Array<{
    strategy: string;
    impact: string;
    timeline: string;
  }>;
  alternative_suppliers: Array<{
    current_country: string;
    alternatives: string[];
    cost_impact: number;
  }>;
  analysis_metadata: {
    confidence_score: number;
    model_used: string;
    created_at: string;
    last_updated: string;
  };
}

export interface ReshoringAnalysisResponse {
  id: string;
  product_id: string;
  target_location: {
    country: string;
    region?: string;
    location_name?: string;
  };
  feasibility_assessment: {
    overall_feasibility: 'HIGH' | 'MEDIUM' | 'LOW';
    feasibility_score: number;
    recommended_strategy: 'FULL' | 'PARTIAL' | 'ASSEMBLY_ONLY' | 'NOT_RECOMMENDED';
  };
  component_analysis: {
    total_components: number;
    high_feasibility: string[];
    medium_feasibility: string[];
    low_feasibility: string[];
    detailed_assessments: Array<{
      component_id: string;
      component_name: string;
      feasibility_category: string;
      current_cost_usd: number;
      reshored_cost_usd: number;
      cost_increase_percentage: number;
      transition_timeline_months: number;
    }>;
  };
  financial_projections: {
    current_cost: number;
    reshored_cost: number;
    cost_difference: number;
    cost_difference_percentage: number;
    investment_breakdown: {
      facility: number;
      equipment: number;
      training: number;
      working_capital: number;
      total: number;
    };
    roi_metrics: {
      payback_period_months: number;
      roi_5_year: number;
      npv_5_year: number;
      irr: number;
    };
  };
  implementation_timeline: {
    total_months: number;
    phases: Array<{
      phase: string;
      months: number;
    }>;
  };
  risk_assessment: {
    overall_score: number;
    risk_categories: {
      technical: string;
      financial: string;
      market: string;
      regulatory: string;
      talent: string;
    };
    key_risks: Array<{
      risk: string;
      probability: number;
      impact: string;
      mitigation: string;
    }>;
    success_factors: string[];
    mitigation_strategies: Array<{
      strategy: string;
      impact: string;
    }>;
  };
  scenario_analysis: {
    best_case: { probability: number; roi: number; timeline_months: number };
    base_case: { probability: number; roi: number; timeline_months: number };
    worst_case: { probability: number; roi: number; timeline_months: number };
  };
  analysis_metadata: {
    confidence_score: number;
    model_used: string;
    is_recommended: boolean;
    created_at: string;
    last_updated: string;
  };
}

export interface AnalysisCostsResponse {
  tier: string;
  current_month_usage: {
    tariff_analyses: number;
    reshoring_analyses: number;
    llm_calls: number;
    llm_cost_usd: number;
  };
  limits: {
    tariff_analyses_per_month: number;
    reshoring_analyses_per_month: number;
  };
  remaining: {
    tariff_analyses: number;
    reshoring_analyses: number;
  };
}

// API Client Class
class ApiClient {
  private baseUrl: string;

  constructor(baseUrl: string = API_V1) {
    this.baseUrl = baseUrl;
  }

  private getAuthToken(): string | null {
    // Get auth token from cookie
    const name = 'auth_token';
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === ' ') c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
    }
    return null;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    
    // Get auth token and add to headers if available
    const authToken = this.getAuthToken();
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };
    
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }
    
    const config: RequestInit = {
      headers: {
        ...headers,
        ...options.headers,
      },
      ...options,
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle rate limiting errors specially
        if (response.status === 429) {
          const error = new Error(errorData.detail?.message || 'Rate limit exceeded');
          (error as any).isRateLimit = true;
          (error as any).rateLimitData = errorData.detail;
          throw error;
        }
        
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`API request failed: ${url}`, error);
      throw error;
    }
  }

  // Product endpoints
  async getProducts(params: {
    skip?: number;
    limit?: number;
    category?: string;
    brand?: string;
    search?: string;
    analysis_status?: string;
  } = {}): Promise<Product[]> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const query = searchParams.toString();
    return this.request<Product[]>(`/products${query ? `?${query}` : ''}`);
  }

  async analyzeProduct(request: ProductAnalysisRequest): Promise<ProductAnalysisResponse | ProductDisambiguationResponse> {
    return this.request<ProductAnalysisResponse | ProductDisambiguationResponse>('/products/analyze', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async confirmProductAnalysis(request: ProductConfirmationRequest): Promise<ProductAnalysisResponse> {
    return this.request<ProductAnalysisResponse>('/products/analyze/confirm', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getProduct(productId: string, view: 'major' | 'all' | 'secondary' = 'major'): Promise<ProductWithComponents> {
    return this.request<ProductWithComponents>(`/products/${productId}?view=${view}`);
  }

  async getProductBySlug(slug: string, view: 'major' | 'all' | 'secondary' = 'major'): Promise<ProductWithComponents> {
    return this.request<ProductWithComponents>(`/products/slug/${slug}?view=${view}`);
  }

  async getProductComponents(
    productId: string,
    tier: 'major' | 'secondary' | 'minor' | 'all' = 'all',
    limit: number = 50
  ): Promise<ComponentSummary[]> {
    return this.request<ComponentSummary[]>(`/products/${productId}/components?tier=${tier}&limit=${limit}`);
  }

  async searchProducts(query: string, limit: number = 10): Promise<SearchResult[]> {
    return this.request<SearchResult[]>(`/products/search/${encodeURIComponent(query)}?limit=${limit}`);
  }

  // Component endpoints
  async getComponent(componentId: string): Promise<Component> {
    return this.request<Component>(`/components/${componentId}`);
  }

  async getComponentBySlug(slug: string): Promise<ComponentDetail> {
    return this.request<ComponentDetail>(`/components/${slug}`);
  }

  async analyzeComponent(slug: string, productContext?: string): Promise<{ message: string; job_id: string; component_id: string; status: string }> {
    return this.request<{ message: string; job_id: string; component_id: string; status: string }>(`/components/${slug}/analyze`, {
      method: 'POST',
      body: JSON.stringify({
        product_context: productContext
      }),
    });
  }

  async searchComponents(query: string, limit: number = 10): Promise<Component[]> {
    return this.request<Component[]>(`/components/search/${encodeURIComponent(query)}?limit=${limit}`);
  }

  // Job endpoints
  async getJob(jobId: string): Promise<BackgroundJob> {
    return this.request<BackgroundJob>(`/jobs/${jobId}`);
  }

  async getJobs(params: {
    skip?: number;
    limit?: number;
    status?: string;
    job_type?: string;
  } = {}): Promise<BackgroundJob[]> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const query = searchParams.toString();
    return this.request<BackgroundJob[]>(`/jobs${query ? `?${query}` : ''}`);
  }

  // Health check (uses base URL, not API_V1)
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const url = `${API_BASE_URL}/health`;
    
    const config: RequestInit = {
      headers: {
        'Content-Type': 'application/json',
      },
      method: 'GET',
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Health check failed: ${url}`, error);
      throw error;
    }
  }

  // Process endpoints
  async getProcesses(params: {
    skip?: number;
    limit?: number;
    category?: string;
    search?: string;
    complexity_level?: string;
    automation_level?: string;
  } = {}): Promise<ProcessDetail[]> {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) {
        searchParams.append(key, value.toString());
      }
    });

    const query = searchParams.toString();
    return this.request<ProcessDetail[]>(`/processes${query ? `?${query}` : ''}`);
  }

  async getProcess(processSlug: string): Promise<ProcessDetail> {
    return this.request<ProcessDetail>(`/processes/${processSlug}`);
  }

  async getProcessBySlug(processSlug: string): Promise<ProcessDetail> {
    return this.request<ProcessDetail>(`/processes/${processSlug}`);
  }

  async getProcessComponents(processSlug: string): Promise<ProcessComponent[]> {
    return this.request<ProcessComponent[]>(`/processes/${processSlug}/components`);
  }

  async searchProcesses(query: string, limit: number = 10): Promise<any[]> {
    return this.request<any[]>(`/processes/search/${encodeURIComponent(query)}?limit=${limit}`);
  }

  // Tariff and Reshoring Analysis endpoints
  async getProductTariffAnalysis(
    productSlug: string, 
    forceRefresh: boolean = false
  ): Promise<{ status: string; data: TariffAnalysisResponse }> {
    const params = new URLSearchParams();
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }
    const query = params.toString();
    return this.request<{ status: string; data: TariffAnalysisResponse }>(
      `/tariff-reshoring/products/${productSlug}/tariff-analysis-by-slug${query ? `?${query}` : ''}`
    );
  }

  async getProductReshoringAnalysis(
    productSlug: string,
    targetCountry: string = 'US',
    targetRegion?: string,
    forceRefresh: boolean = false
  ): Promise<{ status: string; data: ReshoringAnalysisResponse }> {
    const params = new URLSearchParams();
    params.append('target_country', targetCountry);
    if (targetRegion) {
      params.append('target_region', targetRegion);
    }
    if (forceRefresh) {
      params.append('force_refresh', 'true');
    }
    const query = params.toString();
    return this.request<{ status: string; data: ReshoringAnalysisResponse }>(
      `/tariff-reshoring/products/${productSlug}/reshoring-analysis-by-slug${query ? `?${query}` : ''}`
    );
  }

  async getAnalysisCosts(): Promise<{ status: string; data: AnalysisCostsResponse }> {
    return this.request<{ status: string; data: AnalysisCostsResponse }>(
      '/tariff-reshoring/analysis-costs'
    );
  }

  // Admin dashboard endpoints
  async getAdminLLMMetrics(): Promise<any> {
    return this.request<any>('/admin/llm-metrics');
  }

  async getAdminSubscriptionMetrics(): Promise<any> {
    return this.request<any>('/admin/subscription-metrics');
  }

  async getAdminCostAlerts(): Promise<any> {
    return this.request<any>('/admin/cost-alerts');
  }

  async triggerEmergencyAction(action: string): Promise<any> {
    return this.request<any>('/admin/emergency-action', {
      method: 'POST',
      body: JSON.stringify({ action }),
    });
  }

  // Free trial endpoints
  async startFreeTrial(trialData: any): Promise<{ message: string; trial_id: string; expires_at: string }> {
    return this.request<{ message: string; trial_id: string; expires_at: string }>('/auth/start-trial', {
      method: 'POST',
      body: JSON.stringify(trialData),
    });
  }

  // Admin endpoints
  async getAdminLogs(params: {
    skip?: number;
    limit?: number;
    action_type?: string;
    entity_type?: string;
    entity_id?: string;
    user_id?: string;
    include_reverted?: boolean;
  } = {}): Promise<any[]> {
    const queryParams = new URLSearchParams();
    if (params.skip !== undefined) queryParams.append('skip', params.skip.toString());
    if (params.limit !== undefined) queryParams.append('limit', params.limit.toString());
    if (params.action_type) queryParams.append('action_type', params.action_type);
    if (params.entity_type) queryParams.append('entity_type', params.entity_type);
    if (params.entity_id) queryParams.append('entity_id', params.entity_id);
    if (params.user_id) queryParams.append('user_id', params.user_id);
    if (params.include_reverted !== undefined) queryParams.append('include_reverted', params.include_reverted.toString());
    
    const endpoint = `/admin/logs${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
    return this.request<any[]>(endpoint);
  }

  async getAdminStats(): Promise<any> {
    return this.request<any>('/admin/stats');
  }

  async revertAdminAction(logId: string, reason: string, revertedBy?: string): Promise<{ message: string; reverted: boolean }> {
    return this.request<{ message: string; reverted: boolean }>(`/admin/logs/${logId}/revert`, {
      method: 'POST',
      body: JSON.stringify({
        revert_reason: reason,
        reverted_by: revertedBy
      }),
    });
  }

  async updateProductData(productId: string, data: any): Promise<any> {
    return this.request<any>(`/admin/products/${productId}/data`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateComponentData(componentId: string, data: any): Promise<any> {
    return this.request<any>(`/admin/components/${componentId}/data`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async updateProductGeneric(productId: string, data: any, changeSummary?: string, createdBy?: string): Promise<any> {
    return this.request<any>(`/admin/products/${productId}`, {
      method: 'PUT',
      body: JSON.stringify({
        data,
        change_summary: changeSummary,
        created_by: createdBy
      }),
    });
  }

  async updateComponentGeneric(componentId: string, data: any, changeSummary?: string, createdBy?: string): Promise<any> {
    return this.request<any>(`/admin/components/${componentId}`, {
      method: 'PUT',
      body: JSON.stringify({
        data,
        change_summary: changeSummary,
        created_by: createdBy
      }),
    });
  }
}

// Create and export API client instance
export const apiClient = new ApiClient();

// Convenience functions
export const api = {
  // Products
  getProducts: (params?: Parameters<typeof apiClient.getProducts>[0]) => 
    apiClient.getProducts(params),
  
  analyzeProduct: (request: ProductAnalysisRequest) => 
    apiClient.analyzeProduct(request),
  
  confirmProductAnalysis: (request: ProductConfirmationRequest) => 
    apiClient.confirmProductAnalysis(request),
  
  getProduct: (productId: string, view?: 'major' | 'all' | 'secondary') => 
    apiClient.getProduct(productId, view),
  
  getProductBySlug: (slug: string, view?: 'major' | 'all' | 'secondary') => 
    apiClient.getProductBySlug(slug, view),
  
  getProductComponents: (productId: string, tier?: 'major' | 'secondary' | 'minor' | 'all', limit?: number) => 
    apiClient.getProductComponents(productId, tier, limit),
  
  searchProducts: (query: string, limit?: number) => 
    apiClient.searchProducts(query, limit),

  // Components
  getComponent: (componentId: string) => 
    apiClient.getComponent(componentId),
  
  getComponentBySlug: (slug: string) => 
    apiClient.getComponentBySlug(slug),
  
  analyzeComponent: (slug: string, productContext?: string) => 
    apiClient.analyzeComponent(slug, productContext),
  
  searchComponents: (query: string, limit?: number) => 
    apiClient.searchComponents(query, limit),

  // Jobs
  getJob: (jobId: string) => 
    apiClient.getJob(jobId),
  
  getJobs: (params?: Parameters<typeof apiClient.getJobs>[0]) => 
    apiClient.getJobs(params),

  // Health
  healthCheck: () => 
    apiClient.healthCheck(),

  // Process endpoints
  getProcesses: (params?: Parameters<typeof apiClient.getProcesses>[0]) => 
    apiClient.getProcesses(params),
  
  getProcess: (processSlug: string) => 
    apiClient.getProcess(processSlug),
  
  getProcessBySlug: (processSlug: string) => 
    apiClient.getProcess(processSlug),
  
  getProcessComponents: (processSlug: string) => 
    apiClient.getProcessComponents(processSlug),
  
  searchProcesses: (query: string, limit?: number) => 
    apiClient.searchProcesses(query, limit),

  // Tariff and Reshoring Analysis endpoints
  getProductTariffAnalysis: (productSlug: string, forceRefresh?: boolean) => 
    apiClient.getProductTariffAnalysis(productSlug, forceRefresh),
  
  getProductReshoringAnalysis: (productSlug: string, targetCountry?: string, targetRegion?: string, forceRefresh?: boolean) => 
    apiClient.getProductReshoringAnalysis(productSlug, targetCountry, targetRegion, forceRefresh),

  getAnalysisCosts: () => 
    apiClient.getAnalysisCosts(),

  // Admin dashboard endpoints
  getAdminLLMMetrics: () => 
    apiClient.getAdminLLMMetrics(),
  
  getAdminSubscriptionMetrics: () => 
    apiClient.getAdminSubscriptionMetrics(),
  
  getAdminCostAlerts: () => 
    apiClient.getAdminCostAlerts(),
  
  triggerEmergencyAction: (action: string) => 
    apiClient.triggerEmergencyAction(action),

  // Free trial endpoints
  startFreeTrial: (trialData: any) => 
    apiClient.startFreeTrial(trialData),

  // Admin endpoints
  getAdminLogs: (params: {
    skip?: number;
    limit?: number;
    action_type?: string;
    entity_type?: string;
    entity_id?: string;
    user_id?: string;
    include_reverted?: boolean;
  } = {}) => 
    apiClient.getAdminLogs(params),
  
  getAdminStats: () => 
    apiClient.getAdminStats(),
  
  revertAdminAction: (logId: string, reason: string, revertedBy?: string) => 
    apiClient.revertAdminAction(logId, reason, revertedBy),
  
  updateProductData: (productId: string, data: any) => 
    apiClient.updateProductData(productId, data),
  
  updateComponentData: (componentId: string, data: any) => 
    apiClient.updateComponentData(componentId, data),
  
  updateProductGeneric: (productId: string, data: any, changeSummary?: string, createdBy?: string) => 
    apiClient.updateProductGeneric(productId, data, changeSummary, createdBy),
  
  updateComponentGeneric: (componentId: string, data: any, changeSummary?: string, createdBy?: string) => 
    apiClient.updateComponentGeneric(componentId, data, changeSummary, createdBy),
};

export default api; 